import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import { useTranslation } from 'react-i18next';
import {
  ShowToast,
  netState,
  removeSetting,
  retrieveSetting,
  storeSetting,
  storeToken,
} from '../Common/common';
import BTButton from '../Components/BTButton';
import CheckBox from '../Components/Checkbox';
import {useSignInMutation} from '../redux/services/ausersApi';
import BTLinearGradient from '../Components/BTLinearGradient';
import LanguageChips from '../Components/LanguageChips';
import {
  colors,
  spacing,
  shadows,
  borderRadius,
  typography,
} from '../theme/theme';
import packageJson from '../../package.json';
import {
  getAppVersionInfo,
  AppVersionInfo,
  checkAppUpdateStatus,
  checkAppUpdateManually,
  handleUpdate,
  UpdateResult,
} from '../utils/updateChecker';
import UpdateModal from '../Components/UpdateModal';

import T from '../Components/T';
interface ColProps {
  children: ReactNode;
}

interface RowProps {
  children: ReactNode;
}

const Col: React.FC<ColProps> = ({children}) => {
  return <View style={styles.col}>{children}</View>;
};

const Row: React.FC<RowProps> = ({children}) => {
  return <View style={styles.row}>{children}</View>;
};

const App = () => {
  const { t } = useTranslation('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [autoLogin, setAutoLogin] = useState(false);
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  const autoLoginTimerRef = useRef<any>(null);
  const [signIn] = useSignInMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState({
    cm_code: '',
    us_name: '',
    us_pwd: '',
    remember: false,
  });
  const [versionInfo, setVersionInfo] = useState<AppVersionInfo>({
    version: (packageJson && packageJson.version) || '1.0.0',
    buildNumber: '1',
    displayVersion: `v${(packageJson && packageJson.version) || '1.0.0'}`,
  });
  const [updateInfo, setUpdateInfo] = useState<UpdateResult | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initVersionAndCheckUpdate = async () => {
      try {
        const info = await getAppVersionInfo();
        if (isMounted) {
          setVersionInfo(info);
        }

        // Auto check for updates on login screen mount (mobile platforms only)
        if (Platform.OS !== 'web') {
          const updateResult = await checkAppUpdateStatus();
          if (isMounted && updateResult.updateAvailable) {
            setUpdateInfo(updateResult);
            // If critical forced update, show modal immediately
            if (updateResult.isForceUpdate) {
              setShowUpdateModal(true);
            }
          }
        }
      } catch (err) {
        console.warn('Login screen version/update init error:', err);
      }
    };

    initVersionAndCheckUpdate();

    return () => {
      isMounted = false;
      if (autoLoginTimerRef.current) {
        clearTimeout(autoLoginTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const code = await retrieveSetting('hcode');
        const username = await retrieveSetting('username');
        const pwd = await retrieveSetting('pwd');
        const autologinSetting = await retrieveSetting('autologin');
        const autologinPaused = await retrieveSetting('autologin_paused');

        const shouldRemember = Boolean(code);
        const shouldAutoLogin = autologinSetting === 'true';

        if (code) {
          setIsSelected(true);
          setAutoLogin(shouldAutoLogin);
          const restoredState = {
            cm_code: code,
            us_name: username || '',
            us_pwd: pwd || '',
            remember: true,
          };
          setState(restoredState);

          // If auto login is enabled, credentials exist, and user hasn't explicitly logged out this session
          if (
            shouldAutoLogin &&
            autologinPaused !== 'true' &&
            code &&
            username &&
            pwd
          ) {
            setIsAutoLoggingIn(true);
            autoLoginTimerRef.current = setTimeout(() => {
              handleSubmit(restoredState);
            }, 800);
          }
        }
      } catch (error) {
        setIsSelected(false);
        console.error(error);
      }
    };

    fetchSettings();
  }, []);

  const handleCancelAutoLogin = () => {
    if (autoLoginTimerRef.current) {
      clearTimeout(autoLoginTimerRef.current);
      autoLoginTimerRef.current = null;
    }
    setIsAutoLoggingIn(false);
  };

  const handleSubmit = async (customValues?: any) => {
    setIsLoading(true);
    setIsAutoLoggingIn(false);
    if (autoLoginTimerRef.current) {
      clearTimeout(autoLoginTimerRef.current);
      autoLoginTimerRef.current = null;
    }

    // Only use customValues if it's an explicit credentials object, never when it's a React Native event
    const isExplicitCredentials =
      customValues &&
      typeof customValues === 'object' &&
      typeof customValues.cm_code !== 'undefined' &&
      customValues.cm_code !== '' &&
      !customValues.nativeEvent &&
      !customValues._dispatchInstances &&
      !customValues.bubbles;

    const values = isExplicitCredentials ? customValues : state;

    const payload = {
      cm_code: values.cm_code,
      us_name: values.us_name,
      us_pwd: values.us_pwd,
    };

    if (!payload.cm_code || !payload.us_name || !payload.us_pwd) {
      ShowToast('Please enter Company Code, Username and Password', 'warning');
      setIsLoading(false);
      return;
    }

    try {
      const res: any = await signIn(payload);

      // Check if user is already logged in (same as web UI)
      if (res.data?.us_current_session && res.data?.us_multiuser === 0) {
        ShowToast('User already logged in', 'warning');
      }

      if (res.data && res.data.us_token) {
        storeToken(res.data.us_token);
        if (values.remember) {
          await storeSetting('hcode', values.cm_code);
          await storeSetting('username', values.us_name);
          await storeSetting('pwd', values.us_pwd);
          setIsSelected(true);
        } else {
          await removeSetting('hcode');
          await removeSetting('username');
          await removeSetting('pwd');
          await removeSetting('autologin');
          setIsSelected(false);
          setAutoLogin(false);
        }

        if (autoLogin && values.remember) {
          await storeSetting('autologin', 'true');
          await removeSetting('autologin_paused');
        } else {
          await storeSetting('autologin', 'false');
        }

        // Show welcome toast after successful login
        const username = res.data.us_name || values.us_name;
        if (username) {
          setTimeout(() => {
            ShowToast(`Welcome, ${username}!`, 'success');
          }, 300);
        }
      } else {
        const errorMsg =
          (typeof res.error?.data === 'string' ? res.error.data : null) ||
          res.error?.data?.message ||
          res.data?.message ||
          'Invalid Credentials';
        ShowToast(errorMsg, 'error');
      }
    } catch (error: any) {
      // Handle error response - might include session info (same as web UI)
      if (error.data?.us_current_session && error.data?.us_multiuser === 0) {
        ShowToast('User already logged in', 'warning');
        // Still proceed with login if token is available
        if (error.data?.us_token) {
          storeToken(error.data.us_token);
          if (values.remember) {
            await storeSetting('hcode', values.cm_code);
            await storeSetting('username', values.us_name);
            await storeSetting('pwd', values.us_pwd);
            setIsSelected(true);
          }
          if (autoLogin && values.remember) {
            await storeSetting('autologin', 'true');
            await removeSetting('autologin_paused');
          }
          const username = error.data.us_name || values.us_name;
          if (username) {
            setTimeout(() => {
              ShowToast(`Welcome, ${username}!`, 'success');
            }, 300);
          }
        }
      } else {
        const errorMsg =
          (typeof error?.data === 'string' ? error.data : null) ||
          error?.data?.message ||
          error?.message ||
          'Invalid Credentials';
        ShowToast(errorMsg, 'error');
      }
    }
    setIsLoading(false);
  };

  const [networkState, setNetworkState] = useState(true);

  useEffect(() => {
    const getNetState = async () => {
      setNetworkState(await netState());
    };

    getNetState();
  }, []);

  return (
    <BTLinearGradient colors={colors.primaryGradient} style={styles.gradient}>
      <StatusBar barStyle="light-content" />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{flex: 1}}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}>
        <ScrollView
          contentContainerStyle={styles.scrollContainer}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <View style={styles.decorativeCircle1} />
          <View style={styles.decorativeCircle2} />

          <View style={styles.mainContainer}>
            <LanguageChips style={{ marginBottom: 8, justifyContent: 'center' }} />
            {networkState ? null : (
              <View style={styles.networkWarning}>
                <Text style={styles.errorText}>
                  <T>Check Internet Connection.</T> {'\n\n'}<T>To Print please connect to
                  your Wi-fi...</T>
                </Text>
              </View>
            )}

            <View style={styles.headerSection}>
              <View style={styles.logoWrapper}>
                <Image
                  source={require('../assets/bluehms.png')}
                  style={styles.logoImage}
                />
                <View style={styles.logoGlow} />
              </View>
              <Text style={styles.welcomeText}>{t('welcome')}</Text>
              <Text style={styles.subtitleText}><T>Sign in to continue</T></Text>
            </View>

            <View style={styles.formSection}>
              <View style={styles.inputContainer}>
                <FontAwesome
                  name="building"
                  size={20}
                  color={colors.primary}
                  style={styles.inputIcon}
                />
                <TextInput
                  value={state.cm_code}
                  style={styles.inputText}
                  placeholder={t('hotelCode')}
                  placeholderTextColor={colors.mediumGray}
                  onChangeText={text => setState({...state, cm_code: text})}
                  keyboardType="number-pad"
                  autoFocus
                />
              </View>

              <View style={styles.inputContainer}>
                <FontAwesome
                  name="user"
                  size={20}
                  color={colors.primary}
                  style={styles.inputIcon}
                />
                <TextInput
                  value={state.us_name}
                  style={styles.inputText}
                  placeholder={t('username')}
                  placeholderTextColor={colors.mediumGray}
                  onChangeText={text => setState({...state, us_name: text})}
                />
              </View>

              <View style={styles.inputContainer}>
                <FontAwesome
                  name="lock"
                  size={20}
                  color={colors.primary}
                  style={styles.inputIcon}
                />
                <TextInput
                  value={state.us_pwd}
                  secureTextEntry={!showPassword}
                  style={[styles.inputText, styles.passwordInput]}
                  placeholder={t('password')}
                  placeholderTextColor={colors.mediumGray}
                  onChangeText={text => setState({...state, us_pwd: text})}
                />
                <TouchableOpacity
                  style={styles.eyeIcon}
                  onPress={() => setShowPassword(!showPassword)}>
                  <FontAwesome
                    name={showPassword ? 'eye' : 'eye-slash'}
                    size={22}
                    color={colors.primary}
                  />
                </TouchableOpacity>
              </View>

              <View style={styles.rememberSection}>
                <TouchableOpacity
                  style={styles.rememberItem}
                  activeOpacity={0.8}
                  onPress={() => {
                    const nextVal = !isSelected;
                    setIsSelected(nextVal);
                    setState({...state, remember: nextVal});
                    if (!nextVal) {
                      setAutoLogin(false);
                    }
                  }}>
                  <CheckBox
                    label=""
                    value={!!isSelected}
                    onValueChange={val => {
                      setIsSelected(val);
                      setState({...state, remember: val});
                      if (!val) {
                        setAutoLogin(false);
                      }
                    }}
                  />
                  <Text style={styles.checkboxLabel}>{t('rememberMe')}</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.rememberItem}
                  activeOpacity={0.8}
                  onPress={() => {
                    const nextVal = !autoLogin;
                    setAutoLogin(nextVal);
                    if (nextVal) {
                      setIsSelected(true);
                      setState(prev => ({...prev, remember: true}));
                    }
                  }}>
                  <CheckBox
                    label=""
                    value={!!autoLogin}
                    onValueChange={val => {
                      setAutoLogin(val);
                      if (val) {
                        setIsSelected(true);
                        setState(prev => ({...prev, remember: true}));
                      }
                    }}
                  />
                  <Text style={styles.checkboxLabel}><T>Auto Login</T></Text>
                </TouchableOpacity>
              </View>

              {isAutoLoggingIn && (
                <View style={styles.autoLoginBanner}>
                  <Text style={styles.autoLoginText}><T>Auto-logging in...</T></Text>
                  <TouchableOpacity
                    onPress={handleCancelAutoLogin}
                    style={styles.cancelAutoLoginBtn}>
                    <Text style={styles.cancelAutoLoginText}><T>Cancel</T></Text>
                  </TouchableOpacity>
                </View>
              )}

              <BTButton
                onPress={() => handleSubmit()}
                onLongPress={() => {}}
                disabled={isLoading}
                title={t('login').toUpperCase()}
                loading={isLoading}
                color={colors.primary}
                buttonStyle={styles.loginButton}
              />
            </View>

            <View style={styles.footerSection}>
              <TouchableOpacity
                style={styles.versionTouchable}
                activeOpacity={0.7}
                onPress={() => {
                  if (Platform.OS === 'web') {
                    checkAppUpdateManually();
                    return;
                  }
                  if (updateInfo?.updateAvailable) {
                    setShowUpdateModal(true);
                  } else {
                    checkAppUpdateManually(result => {
                      setUpdateInfo(result);
                      setShowUpdateModal(true);
                    });
                  }
                }}>
                <Text style={styles.footerText}>
                  © {new Date().getFullYear()} <T>BlueHMS • v</T>{versionInfo.version}
                </Text>

                {Platform.OS !== 'web' && updateInfo?.updateAvailable ? (
                  <View
                    style={[
                      styles.updateBadge,
                      updateInfo.isForceUpdate && styles.updateBadgeForce,
                    ]}>
                    <FontAwesome
                      name="arrow-circle-up"
                      size={12}
                      color="#FFFFFF"
                      style={styles.updateBadgeIcon}
                    />
                    <Text style={styles.updateBadgeText}>
                      {updateInfo.isForceUpdate
                        ? 'Update Required'
                        : 'Update Available'}
                    </Text>
                  </View>
                ) : (
                  <Text style={styles.checkUpdateHint}><T>Tap to check for updates</T></Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Global Update Modal when triggered from login */}
      {Platform.OS !== 'web' && updateInfo && (
        <UpdateModal
          visible={showUpdateModal}
          isForceUpdate={updateInfo.isForceUpdate}
          currentVersion={updateInfo.currentVersion}
          latestVersion={updateInfo.latestVersion}
          releaseNotes={updateInfo.releaseNotes}
          onUpdate={() => {
            handleUpdate(updateInfo.storeUrl);
          }}
          onLater={() => {
            setShowUpdateModal(false);
          }}
        />
      )}
    </BTLinearGradient>
  );
};

const styles = StyleSheet.create({
  gradient: {
    flex: 1,
    width: '100%',
  },
  scrollContainer: {
    flexGrow: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing['5xl'],
    paddingBottom: 80,
  },
  decorativeCircle1: {
    position: 'absolute',
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    top: -100,
    right: -100,
  },
  decorativeCircle2: {
    position: 'absolute',
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    bottom: -50,
    left: -50,
  },
  mainContainer: {
    width: '100%',
    maxWidth: 450,
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderRadius: borderRadius['3xl'],
    padding: spacing['2xl'],
    shadowColor: colors.black,
    shadowOffset: {width: 0, height: 20},
    shadowOpacity: 0.3,
    shadowRadius: 40,
    elevation: 20,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(10px)',
      },
    }),
  },
  networkWarning: {
    marginBottom: spacing.lg,
  },
  headerSection: {
    alignItems: 'center',
    marginBottom: spacing['5xl'],
  },
  logoWrapper: {
    position: 'relative',
    marginBottom: spacing.base,
  },
  logoImage: {
    width: 80,
    height: 80,
    zIndex: 2,
  },
  logoGlow: {
    position: 'absolute',
    width: 80,
    height: 80,
    borderRadius: borderRadius['5xl'],
    backgroundColor: 'rgba(102, 126, 234, 0.2)',
    top: 0,
    left: 0,
    zIndex: 1,
  },
  welcomeText: {
    fontSize: typography.fontSize['4xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    marginBottom: spacing.sm,
    letterSpacing: typography.letterSpacing.tight,
  },
  subtitleText: {
    fontSize: typography.fontSize.base,
    color: colors.textTertiary,
    fontWeight: typography.fontWeight.regular,
  },
  formSection: {
    width: '100%',
    marginBottom: spacing['2xl'],
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: borderRadius.lg,
    height: 60,
    marginBottom: spacing.base,
    paddingLeft: spacing.lg,
    paddingRight: spacing.base,
    borderWidth: 2,
    borderColor: 'transparent',
    ...shadows.md,
  },
  inputIcon: {
    marginRight: spacing.md,
  },
  inputText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.charcoal,
    fontWeight: typography.fontWeight.medium,
  },
  passwordInput: {
    paddingRight: spacing['5xl'],
  },
  eyeIcon: {
    position: 'absolute',
    right: spacing.lg,
    padding: 5,
  },
  rememberSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
    paddingHorizontal: 4,
  },
  rememberItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  checkboxLabel: {
    color: colors.textTertiary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.sm,
    paddingLeft: spacing.xs,
  },
  autoLoginBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(3, 201, 136, 0.12)',
    borderRadius: borderRadius.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: '#03C988',
  },
  autoLoginText: {
    color: '#03C988',
    fontWeight: typography.fontWeight.bold,
    fontSize: typography.fontSize.sm,
  },
  cancelAutoLoginBtn: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    backgroundColor: '#FF2171',
    borderRadius: borderRadius.sm,
  },
  cancelAutoLoginText: {
    color: '#fff',
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
  },
  loginButton: {
    borderRadius: borderRadius.lg,
    height: 60,
    ...shadows.colored,
  },
  footerSection: {
    alignItems: 'center',
    paddingTop: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
  },
  footerText: {
    fontSize: typography.fontSize.xs + 1,
    color: colors.mediumGray,
    fontWeight: typography.fontWeight.regular,
  },
  versionTouchable: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  updateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#03C988',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    marginTop: spacing.xs,
  },
  updateBadgeForce: {
    backgroundColor: '#FF2171',
  },
  updateBadgeIcon: {
    marginRight: 4,
  },
  updateBadgeText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: 'bold',
  },
  checkUpdateHint: {
    fontSize: 10,
    color: colors.primary,
    marginTop: 3,
    opacity: 0.85,
  },
  row: {
    flexDirection: 'row',
  },
  col: {
    flex: 1,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.error,
    backgroundColor: colors.errorLight,
    padding: spacing.base,
    borderRadius: borderRadius.base,
    textAlign: 'center',
    borderWidth: 1,
    borderColor: '#FECACA',
  },
});

export default App;

